import React, { useState, useRef, useCallback, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiZoomIn, FiZoomOut, FiMaximize2, FiMove } = FiIcons;

const clamp = (v, min, max) => Math.min(Math.max(v, min), max);

function orthoPath(fx, fy, tx, ty) {
  const mx = (fx + tx) / 2;
  return `M${fx},${fy} L${mx},${fy} L${mx},${ty} L${tx},${ty}`;
}

export default function WorkflowCanvas({ nodes: initialNodes, edges, title, label, canvasHeight = 500 }) {
  const [zoom, setZoom] = useState(0.78);
  const [pan, setPan] = useState({ x: 24, y: 24 });
  const [panning, setPanning] = useState(false);
  const [activeNode, setActiveNode] = useState(null);
  const [nodePositions, setNodePositions] = useState(() => {
    const pos = {};
    initialNodes.forEach(n => { pos[n.id] = { x: n.x, y: n.y }; });
    return pos;
  });

  const panStart = useRef(null);
  const canvasRef = useRef(null);
  const draggingNode = useRef(null);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    setZoom(z => clamp(z - e.deltaY * 0.0008, 0.3, 2.2));
  }, []);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  const onCanvasMouseDown = (e) => {
    if (e.target.closest('.wf-node')) return;
    setPanning(true);
    panStart.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };

  const onNodeMouseDown = (e, nodeId) => {
    e.stopPropagation();
    const rect = canvasRef.current.getBoundingClientRect();
    const mx = (e.clientX - rect.left - pan.x) / zoom;
    const my = (e.clientY - rect.top - pan.y) / zoom;
    draggingNode.current = nodeId;
    dragOffset.current = { x: mx - nodePositions[nodeId].x, y: my - nodePositions[nodeId].y };
    setActiveNode(nodeId);
  };

  const onMouseMove = (e) => {
    if (draggingNode.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      const mx = (e.clientX - rect.left - pan.x) / zoom;
      const my = (e.clientY - rect.top - pan.y) / zoom;
      setNodePositions(prev => ({
        ...prev,
        [draggingNode.current]: { x: mx - dragOffset.current.x, y: my - dragOffset.current.y }
      }));
      return;
    }
    if (panning && panStart.current) {
      setPan({ x: e.clientX - panStart.current.x, y: e.clientY - panStart.current.y });
    }
  };

  const onMouseUp = () => {
    draggingNode.current = null;
    setPanning(false);
  };

  const resetView = () => {
    setZoom(0.78);
    setPan({ x: 24, y: 24 });
    const pos = {};
    initialNodes.forEach(n => { pos[n.id] = { x: n.x, y: n.y }; });
    setNodePositions(pos);
  };

  const nodes = initialNodes.map(n => ({ ...n, ...nodePositions[n.id] }));
  const cursor = draggingNode.current ? 'grabbing' : panning ? 'grabbing' : 'default';
  const uid = label.replace(/\s+/g, '-');
  const innerH = canvasHeight - 48;

  return (
    <div className="w-full rounded-2xl overflow-hidden" style={{ background: '#ffffff', border: '1px solid #e2e8f0', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', height: canvasHeight }}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: '#e2e8f0', background: '#ffffff' }}>
        <div className="flex items-center gap-3">
          <div className="flex gap-1">
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#f87171' }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#fbbf24' }} />
            <div className="w-2.5 h-2.5 rounded-full" style={{ background: '#4ade80' }} />
          </div>
          <div className="w-px h-4 mx-1" style={{ background: '#e2e8f0' }} />
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: '#94a3b8' }}>{label}</span>
          <span className="text-xs" style={{ color: '#cbd5e1' }}>·</span>
          <span className="text-xs font-medium" style={{ color: '#94a3b8' }}>{title}</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setZoom(z => clamp(z - 0.1, 0.3, 2.2))} className="p-1.5 rounded-md" style={{ color: '#94a3b8' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <SafeIcon icon={FiZoomOut} className="w-3.5 h-3.5" />
          </button>
          <span className="text-xs font-mono px-2 tabular-nums" style={{ color: '#94a3b8', minWidth: 38, textAlign: 'center' }}>
            {Math.round(zoom * 100)}%
          </span>
          <button onClick={() => setZoom(z => clamp(z + 0.1, 0.3, 2.2))} className="p-1.5 rounded-md" style={{ color: '#94a3b8' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <SafeIcon icon={FiZoomIn} className="w-3.5 h-3.5" />
          </button>
          <div className="w-px h-4 mx-1" style={{ background: '#e2e8f0' }} />
          <button onClick={resetView} className="p-1.5 rounded-md" style={{ color: '#94a3b8' }}
            onMouseEnter={e => e.currentTarget.style.background = '#f1f5f9'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <SafeIcon icon={FiMaximize2} className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        className="relative overflow-hidden select-none"
        style={{ height: innerH, cursor, background: '#ffffff' }}
        onMouseDown={onCanvasMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}>

        {/* Grid */}
        <svg className="absolute inset-0 w-full h-full" style={{ pointerEvents: 'none' }}>
          <defs>
            <pattern id={`sg-${uid}`} width={20 * zoom} height={20 * zoom} patternUnits="userSpaceOnUse" x={pan.x % (20 * zoom)} y={pan.y % (20 * zoom)}>
              <path d={`M ${20 * zoom} 0 L 0 0 0 ${20 * zoom}`} fill="none" stroke="#e2e8f0" strokeWidth="0.5" />
            </pattern>
            <pattern id={`lg-${uid}`} width={100 * zoom} height={100 * zoom} patternUnits="userSpaceOnUse" x={pan.x % (100 * zoom)} y={pan.y % (100 * zoom)}>
              <rect width={100 * zoom} height={100 * zoom} fill={`url(#sg-${uid})`} />
              <path d={`M ${100 * zoom} 0 L 0 0 0 ${100 * zoom}`} fill="none" stroke="#cbd5e1" strokeWidth="1" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill={`url(#lg-${uid})`} />
        </svg>

        {/* Transform layer */}
        <div style={{ transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})`, transformOrigin: '0 0', position: 'absolute', top: 0, left: 0 }}>
          {/* Edges SVG */}
          <svg style={{ position: 'absolute', top: 0, left: 0, width: 1800, height: 1200, overflow: 'visible', pointerEvents: 'none' }}>
            <defs>
              <marker id={`arr-${uid}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0.5 L0,5.5 L7,3 z" fill="#cbd5e1" />
              </marker>
              <marker id={`arr-act-${uid}`} markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto">
                <path d="M0,0.5 L0,5.5 L7,3 z" fill="#22c55e" />
              </marker>
            </defs>
            {edges.map((edge, i) => {
              const from = nodes.find(n => n.id === edge.from);
              const to = nodes.find(n => n.id === edge.to);
              if (!from || !to) return null;
              const fh = from.h || 76;
              const fw = from.w || 160;
              const th = to.h || 76;
              const fx = from.x + fw;
              const fy = from.y + fh / 2;
              const tx = to.x;
              const ty = to.y + th / 2;
              const isActive = activeNode === edge.from || activeNode === edge.to;
              const color = isActive ? '#22c55e' : '#d1d5db';
              const strokeW = isActive ? 2 : 1.5;
              const d = orthoPath(fx, fy, tx, ty);
              const mx = (fx + tx) / 2;
              return (
                <g key={i}>
                  <path d={d} fill="none" stroke={color} strokeWidth={strokeW}
                    strokeDasharray={edge.dashed ? '5,4' : undefined}
                    markerEnd={`url(#arr${isActive ? '-act' : ''}-${uid})`}
                    style={{ transition: 'stroke 0.15s, stroke-width 0.15s' }} />
                  {edge.label && (
                    <g>
                      <rect x={mx - 14} y={Math.min(fy, ty) - 18} width={28} height={14} rx={3} fill={isActive ? '#dcfce7' : '#f1f5f9'} />
                      <text x={mx} y={Math.min(fy, ty) - 7} textAnchor="middle" fontSize={8}
                        fill={isActive ? '#16a34a' : '#94a3b8'} fontFamily="ui-monospace,monospace" fontWeight="700">
                        {edge.label}
                      </text>
                    </g>
                  )}
                </g>
              );
            })}
          </svg>

          {/* Nodes */}
          {nodes.map(node => (
            <WorkflowNode key={node.id} node={node} active={activeNode === node.id}
              onMouseDown={(e) => onNodeMouseDown(e, node.id)}
              onMouseEnter={() => !draggingNode.current && setActiveNode(node.id)}
              onMouseLeave={() => !draggingNode.current && setActiveNode(null)} />
          ))}
        </div>

        {/* Hint bar */}
        <div className="absolute bottom-0 left-0 right-0 flex items-center gap-2 px-4 py-2 pointer-events-none"
          style={{ borderTop: '1px solid #f1f5f9', background: 'rgba(255,255,255,0.9)' }}>
          <SafeIcon icon={FiMove} className="w-3 h-3" style={{ color: '#cbd5e1' }} />
          <span className="text-xs" style={{ color: '#cbd5e1' }}>
            drag canvas to pan · scroll to zoom · drag nodes to rearrange
          </span>
        </div>
      </div>
    </div>
  );
}

function WorkflowNode({ node, active, onMouseDown, onMouseEnter, onMouseLeave }) {
  const w = node.w || 160;
  const accentColor = node.color || '#22c55e';
  return (
    <div className="wf-node absolute"
      style={{ left: node.x, top: node.y, width: w, cursor: active ? 'grabbing' : 'grab', userSelect: 'none', zIndex: active ? 10 : 1 }}
      onMouseDown={onMouseDown} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <div style={{
        position: 'relative', background: '#ffffff',
        border: `1.5px solid ${active ? accentColor : '#e2e8f0'}`,
        borderRadius: 12, padding: '10px 12px',
        boxShadow: active ? `0 0 0 3px ${accentColor}20, 0 4px 20px rgba(0,0,0,0.10)` : '0 1px 4px rgba(0,0,0,0.05)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        display: 'flex', flexDirection: 'column', gap: 5,
      }}>
        <div style={{ position: 'absolute', top: 0, left: 10, right: 10, height: 2, background: active ? accentColor : 'transparent', borderRadius: '0 0 2px 2px', transition: 'background 0.15s' }} />
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          {node.icon && (
            <div style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: active ? accentColor + '18' : '#f8fafc', border: `1px solid ${active ? accentColor + '30' : '#f1f5f9'}`, marginTop: 1 }}>
              <span style={{ fontSize: 12, lineHeight: 1 }}>{node.icon}</span>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 4 }}>
              <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1.3, color: '#1e293b', wordBreak: 'break-word', whiteSpace: 'normal' }}>
                {node.label}
              </span>
              {node.badge && (
                <span style={{ flexShrink: 0, background: node.badgeBg || '#f1f5f9', color: node.badgeColor || '#64748b', fontSize: 7, fontWeight: 700, padding: '2px 5px', borderRadius: 4, letterSpacing: '0.03em', lineHeight: 1.4, whiteSpace: 'nowrap', marginTop: 1 }}>
                  {node.badge}
                </span>
              )}
            </div>
          </div>
        </div>
        {node.desc && (
          <p style={{ fontSize: 10, lineHeight: 1.4, color: '#94a3b8', margin: 0, wordBreak: 'break-word' }}>
            {node.desc}
          </p>
        )}
        {node.tags && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 2 }}>
            {node.tags.map((t, i) => (
              <span key={i} style={{ background: active ? accentColor + '14' : '#f1f5f9', color: active ? accentColor : '#94a3b8', fontSize: 8, fontWeight: 600, padding: '2px 5px', borderRadius: 3, border: `1px solid ${active ? accentColor + '25' : 'transparent'}` }}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}